import { GeneralDataFacade } from '@/_store/general/general.facade';
import { OffersFacade } from '@/_store/offers/offers.facade';
import { BaseFilter, BaseFilterStop, OfferDateRange, Selected } from '@/_store/offers/offers.types';
import { replacePolishLetters } from '@/_utils/strings/replace-polish-letters';
import {
	CheckboxFilterValue,
	MultiChoiceComponent,
} from '@/new-top-search/top-search-dropdown-item/multi-choice/multi-choice.component';
import { TopSearchDatePickerComponent } from '@/new-top-search/top-search-dropdown-item/top-search-date-picker/top-search-date-picker.component';
import {
	CounteringModes,
	DropdownType,
	MultiChoiceModes,
	Participant,
	Row,
	RowValue,
	TopSearchDropdownItemMode,
	TopSearchFilterElType,
} from '@/new-top-search/top-search-dropdown-item/top-search-dropdown-item.types';
import { TransportKind } from '@/offer/offer.model';
import {
	ChangeDetectionStrategy,
	Component,
	EventEmitter,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	computed,
	input,
	signal,
} from '@angular/core';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { DateRange } from '@angular/material/datepicker';
import { BehaviorSubject, Observable, Subject, combineLatest, map, of, takeUntil } from 'rxjs';
import { PriceRange } from './price-filter/price-filter.component';

interface Search {
	show: boolean;
	options?: {
		placeholder?: string;
	};
}

@Component({
	selector: 'app-top-search-dropdown-item',
	templateUrl: './top-search-dropdown-item.component.html',
	styleUrls: ['./top-search-dropdown-item.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class.dropdown-item-host--modal]': '!inlineLayout()',
	},
})
export class TopSearchDropdownItemComponent implements OnInit, OnDestroy {
	mode = input<TopSearchDropdownItemMode>();

	readonly inlineLayout = input(false);

	@Output() valueChange: EventEmitter<boolean> = new EventEmitter();

	@Output() triggerClose: EventEmitter<void> = new EventEmitter();

	@ViewChild(TopSearchDatePickerComponent, { static: false })
	topSearchDatePickerComponent: TopSearchDatePickerComponent;

	@ViewChild(MultiChoiceComponent, { static: false })
	multiChoiceComponent: MultiChoiceComponent;

	isMobile$ = this.generalFacade.isMobile$;
	readonly multiChoiceContainerHeight$ = this.isMobile$.pipe(
		map((isMobile) => this.resolveMultiChoiceContainerHeight(isMobile)),
	);
	type!: DropdownType;
	DropdownType = DropdownType;
	offersDateRange$: Observable<DateRange<Date>> = this.offersFacade.offersDateRange$;
	selectedDateRange$: Observable<DateRange<Date>> = this.offersFacade.selectedDateRange$;
	rows$: Observable<Row[]> = of([]);
	counters$: Observable<{ [key: number]: number }> = of({});
	searchingValue$: BehaviorSubject<string> = new BehaviorSubject<string>('');
	searchBarDropdownOpened = signal(false);
	selected$ = this.offersFacade.selected$.pipe(
		map((selected: Selected) => selected[this.mode() as keyof Selected] as number[]),
	);
	options$: Observable<BaseFilter[]> = of([]);
	airports$: Observable<BaseFilterStop[]> = of([]);
	busStops$: Observable<BaseFilterStop[]> = of([]);
	priceRange$ = this.offersFacade.priceRange$;
	selectedTransport = signal({
		coach: true,
		plane: true,
		ownTransport: false,
	});
	search = computed(
		() =>
			({
				show:
					this.mode() === TopSearchDropdownItemMode.STOPS ||
					this.mode() === TopSearchDropdownItemMode.LOCATIONS,
				options: { placeholder: 'Wyszukaj lokalizacje' },
			}) as Search,
	);

	readonly TopSearchDropdownItemMode = TopSearchDropdownItemMode;
	readonly TopSearchFilterElType = TopSearchFilterElType;
	readonly TransportKind = TransportKind;

	/** Includes 120px reserved in multi-choice max-height calc (search / header). */
	private readonly inlineMultiChoiceHeightPx = 340;
	private readonly inlineLocationsMultiChoiceHeightPx = 380;

	private readonly destroy$: Subject<void> = new Subject();

	constructor(
		private readonly offersFacade: OffersFacade,
		private readonly generalFacade: GeneralDataFacade,
	) {}

	ngOnInit(): void {
		this.options$ = this.setMultiChoiceOptions();
		this.busStops$ = this.options$.pipe(
			map((options) => (options as BaseFilterStop[]).filter((option) => option.kind === TransportKind.COUCH)),
		);
		this.airports$ = this.options$.pipe(
			map((options) => (options as BaseFilterStop[]).filter((option) => option.kind === TransportKind.PLANE)),
		);
		this.setCounterValues();
		this.setDropdownType();
		this.offersFacade.transportKind$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
			this.selectedTransport.set({
				coach: value.includes(TransportKind.COUCH),
				plane: value.includes(TransportKind.PLANE),
				ownTransport: value.includes(TransportKind.OWN_TRANSPORT),
			});
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	filtersActions(filter: CheckboxFilterValue | OfferDateRange | RowValue): void {
		filter = filter as CheckboxFilterValue;
		switch (this.mode()) {
			case TopSearchDropdownItemMode.LOCATIONS:
				if (filter.value) this.offersFacade.addLocationsFilterOption(filter.id);
				else this.offersFacade.removeLocationsFilterOption(filter.id);
				break;
			case TopSearchDropdownItemMode.KINDS:
				if (filter.value) this.offersFacade.addKindsFilterOption(filter.id);
				else this.offersFacade.removeKindsFilterOption(filter.id);
				break;
			case TopSearchDropdownItemMode.BOARDS:
				if (filter.value) this.offersFacade.addBoardsFilterOption(filter.id);
				else this.offersFacade.removeBoardsFilterOption(filter.id);
				break;
			case TopSearchDropdownItemMode.CONVENIENCES:
				if (filter.value) this.offersFacade.addConveniencesFilterOption(filter.id);
				else this.offersFacade.removeConveniencesFilterOption(filter.id);
				break;
			case TopSearchDropdownItemMode.STOPS:
				if (filter.value) this.offersFacade.addStopsFilterOption(filter.id);
				else this.offersFacade.removeStopsFilterOption(filter.id);
				break;
			case TopSearchDropdownItemMode.DATE_RANGE:
				filter = filter as unknown as OfferDateRange;
				this.offersFacade.updateOffersDateRange(filter);
				break;
			case TopSearchDropdownItemMode.PARTICIPANTS:
				filter = filter as unknown as RowValue;
				if (filter.type === Participant.ADULTS) {
					this.offersFacade.updateAdultsCount(filter.value);
				}
				if (filter.type === Participant.CHILDREN) {
					this.offersFacade.updateChildrenCount(filter.value);
				}
				break;
			case TopSearchDropdownItemMode.NIGHTS:
				filter = filter as unknown as RowValue;
				this.offersFacade.updateNights(filter);
				this.offersFacade.updateNightsOptions(filter);
				break;
			case TopSearchDropdownItemMode.SEARCH:
				break;
			default:
				const value: never = this.mode() as never;
				throw new Error(`There is no option like ${value}`);
		}
		this.valueChange?.emit(true);
	}

	clearFilterValue(): void {
		switch (this.type) {
			case DropdownType.COUNTER:
				if (this.mode() === TopSearchDropdownItemMode.NIGHTS) {
					this.offersFacade.setDefaultNights();
				}
				if (this.mode() === TopSearchDropdownItemMode.PARTICIPANTS) {
					this.offersFacade.setDefaultAdultValue();
					this.offersFacade.setDefaultChildrenValue();
				}
				break;
			case DropdownType.MULTI:
				this.multiChoiceComponent.toggleAll(false);
				break;
			case DropdownType.DATE:
				this.topSearchDatePickerComponent.clearDateRange();
				break;
			case DropdownType.SEARCH:
				this.offersFacade.clearLocationsFilter();
				break;
			case DropdownType.PRICE:
				this.offersFacade.clearPriceRange();
				break;
			default:
				const value: never = this.type;
				throw new Error(`There is no type like ${value}`);
		}
	}

	closeDropdown(): void {
		this.valueChange?.emit(true);
		this.triggerClose.emit();
	}

	updateTransportKind(value: MatCheckboxChange, transportKind: TransportKind): void {
		this.offersFacade.updateTransportKind(transportKind, value.checked);
		if (!value.checked) {
			this.offersFacade.clearStopsByTransportKind(transportKind);
		}
		this.valueChange?.emit(true);
	}

	priceRangeChanged(priceChange: PriceRange): void {
		this.offersFacade.updatePriceRange(priceChange);
		this.valueChange?.emit(true);
	}

	private resolveMultiChoiceContainerHeight(isMobile: boolean): string | null {
		if (this.inlineLayout()) {
			if (isMobile) {
				return this.mode() === TopSearchDropdownItemMode.LOCATIONS
					? `${this.inlineLocationsMultiChoiceHeightPx}px`
					: `${this.inlineMultiChoiceHeightPx}px`;
			}

			return '400px';
		}

		if (isMobile) {
			return null;
		}

		return '400px';
	}

	private setMultiChoiceOptions(): Observable<BaseFilter[]> {
		return combineLatest([this.searchingValue$, this.offersFacade.getOptions(this.mode())]).pipe(
			map(([searching, options]) =>
				options.filter((obj) =>
					replacePolishLetters(obj.name.toLowerCase()).includes(
						replacePolishLetters(searching.toLowerCase()),
					),
				),
			),
		);
	}

	private setCounterValues(): void {
		if (this.mode() === TopSearchDropdownItemMode.NIGHTS) {
			this.rows$ = this.offersFacade.nightsRows$;
			this.counters$ = this.offersFacade.nightCounters$;
		} else {
			this.rows$ = this.offersFacade.participantsRows$;
			this.counters$ = this.offersFacade.participantCounters$;
		}
	}

	private setDropdownType(): void {
		switch (true) {
			case MultiChoiceModes.includes(this.mode()):
				this.type = DropdownType.MULTI;
				break;
			case CounteringModes.includes(this.mode()):
				this.type = DropdownType.COUNTER;
				break;
			case this.mode() === TopSearchDropdownItemMode.DATE_RANGE:
				this.type = DropdownType.DATE;
				break;
			case this.mode() === TopSearchDropdownItemMode.SEARCH:
				this.type = DropdownType.SEARCH;
				break;
			case this.mode() === TopSearchDropdownItemMode.PRICE:
				this.type = DropdownType.PRICE;
				break;
			default:
				const value: never = this.mode() as unknown as never;
				throw new Error(`There is no option like ${value}`);
		}
	}
}
